import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';

/**
 * AdminQuestDashboard - Admin-only view of all weekly quests across all households.
 * Allows manual progress overrides.
 */
const AdminQuestDashboard = () => {
    const [quests, setQuests] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [activeTab, setActiveTab] = useState('quests'); // 'quests' | 'templates'
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [overrideModal, setOverrideModal] = useState(null); // { quest, assignee }
    const [overrideProgress, setOverrideProgress] = useState('');
    const [overrideLoading, setOverrideLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    const fetchTemplates = useCallback(async () => {
        try {
            const res = await axios.get(`/api/v1/quests/templates`);
            setTemplates(res.data || []);
        } catch (err) {
            console.error('Failed to fetch templates:', err);
        }
    }, []);

    const fetchQuests = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/v1/admin/quests?page=${page}`);
            setQuests(res.data.quests || []);
            setTotal(res.data.total || 0);
        } catch (err) {
            console.error('Failed to fetch admin quests:', err);
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        if (activeTab === 'quests') fetchQuests();
        if (activeTab === 'templates' && templates.length === 0) fetchTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, page, fetchQuests, fetchTemplates, templates.length]);

    const handleOverride = async () => {
        if (!overrideModal) return;
        const newProgress = parseInt(overrideProgress, 10);
        if (isNaN(newProgress) || newProgress < 0) return;

        setOverrideLoading(true);
        setErrorMsg('');
        try {
            await axios.post(`/api/v1/admin/quests/${overrideModal.quest.id}/override`, {
                kid_user_id: overrideModal.assignee.kid_user_id,
                new_progress: newProgress,
            });
            setSuccessMsg(`✅ Progress updated for ${overrideModal.assignee.kid_username}`);
            setOverrideModal(null);
            setOverrideProgress('');
            await fetchQuests();
        } catch (err) {
            console.error('Override failed:', err);
            const message = err.response && err.response.data
                ? (typeof err.response.data === 'string' ? err.response.data : (err.response.data.error || err.response.data.message))
                : err.message || 'Override failed. Please try again.';
            setErrorMsg(`❌ Failed: ${message}`);
        } finally {
            setOverrideLoading(false);
        }
    };

    return (
        <div style={{ padding: '0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <h2
                        onClick={() => setActiveTab('quests')}
                        style={{ fontSize: '1.4rem', color: activeTab === 'quests' ? '#1f2937' : '#9ca3af', margin: 0, cursor: 'pointer' }}
                    >🛠️ All Quests</h2>
                    <h2
                        onClick={() => setActiveTab('templates')}
                        style={{ fontSize: '1.4rem', color: activeTab === 'templates' ? '#1f2937' : '#9ca3af', margin: 0, cursor: 'pointer' }}
                    >🎁 Templates</h2>
                </div>
                {activeTab === 'quests' && <span style={{ fontSize: '14px', color: '#6b7280' }}>{total} total</span>}
                {activeTab === 'templates' && <span style={{ fontSize: '14px', color: '#6b7280' }}>{templates.length} templates</span>}
            </div>

            {successMsg && (
                <div style={{ background: '#d1fae5', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#065f46', fontWeight: '600' }}>
                    {successMsg}
                    <button onClick={() => setSuccessMsg('')} style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', color: '#065f46' }}>✕</button>
                </div>
            )}
            {errorMsg && (
                <div style={{ background: '#fee2e2', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#991b1b', fontWeight: '600' }}>
                    {errorMsg}
                    <button onClick={() => setErrorMsg('')} style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b' }}>✕</button>
                </div>
            )}

            {loading && activeTab === 'quests' ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>Loading quests...</div>
            ) : activeTab === 'templates' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {templates.map(t => (
                        <div key={t.id} style={{
                            background: '#fff', borderRadius: '16px', padding: '20px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <span style={{ fontSize: '24px', marginRight: '10px' }}>{t.emoji}</span>
                                    <strong style={{ fontSize: '16px', color: '#1f2937' }}>{t.title}</strong>
                                    <span style={{ marginLeft: '8px', background: '#dbeafe', color: '#1e40af', fontSize: '11px', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                                        {t.season}
                                    </span>
                                </div>
                            </div>
                            <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '12px' }}>{t.description}</p>
                            <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '12px', display: 'flex', gap: '16px' }}>
                                <span>🎯 {t.target_type}: {t.target_value}</span>
                                <span style={{ color: '#10b981', fontWeight: '500' }}>💸 +{t.bonus_reward} DB</span>
                            </div>
                        </div>
                    ))}
                </div>
            ) : quests.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>No quests found.</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {quests.map(quest => (
                        <div key={quest.id} style={{
                            background: '#fff',
                            borderRadius: '16px',
                            padding: '20px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                            border: '1px solid #e5e7eb',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                <div>
                                    <span style={{ fontSize: '24px', marginRight: '10px' }}>{quest.emoji}</span>
                                    <strong style={{ fontSize: '16px', color: '#1f2937' }}>{quest.title}</strong>
                                    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                        {quest.target_type} · target: {quest.target_value} · bonus: ${(quest.bonus_reward / 100).toFixed(2)}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                                        Household: {quest.household_id?.slice(0, 8)}... · Status: <strong>{quest.status}</strong>
                                    </div>
                                </div>
                            </div>

                            {/* Assignees */}
                            {(quest.assignees || []).length > 0 ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                            <th style={{ textAlign: 'left', padding: '8px 4px', color: '#6b7280', fontWeight: '600' }}>Kid</th>
                                            <th style={{ textAlign: 'left', padding: '8px 4px', color: '#6b7280', fontWeight: '600' }}>Progress</th>
                                            <th style={{ textAlign: 'left', padding: '8px 4px', color: '#6b7280', fontWeight: '600' }}>%</th>
                                            <th style={{ textAlign: 'left', padding: '8px 4px', color: '#6b7280', fontWeight: '600' }}>Status</th>
                                            <th style={{ padding: '8px 4px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quest.assignees.map(a => (
                                            <tr key={a.kid_user_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                <td style={{ padding: '8px 4px', color: '#374151' }}>{a.kid_username || a.kid_user_id.slice(0, 8)}</td>
                                                <td style={{ padding: '8px 4px', color: '#374151' }}>{a.current_progress}/{quest.target_value}</td>
                                                <td style={{ padding: '8px 4px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{ background: '#e5e7eb', borderRadius: '4px', height: '8px', width: '80px', overflow: 'hidden' }}>
                                                            <div style={{ background: a.is_complete ? '#10b981' : '#6366f1', width: `${a.percent_complete}%`, height: '100%' }} />
                                                        </div>
                                                        <span>{a.percent_complete}%</span>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '8px 4px' }}>
                                                    {a.is_complete ? <span style={{ color: '#10b981', fontWeight: '600' }}>✅ Complete</span> : <span style={{ color: '#f59e0b' }}>In progress</span>}
                                                </td>
                                                <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                                                    <button
                                                        onClick={() => { setOverrideModal({ quest, assignee: a }); setOverrideProgress(String(a.current_progress)); }}
                                                        style={{ fontSize: '12px', background: '#f3f4f6', border: 'none', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', color: '#374151' }}
                                                    >Override</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ fontSize: '13px', color: '#9ca3af' }}>No kids assigned</div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {activeTab === 'quests' && total > 50 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', cursor: page <= 1 ? 'default' : 'pointer', background: page <= 1 ? '#f3f4f6' : '#fff' }}>← Prev</button>
                    <span style={{ padding: '8px 16px', color: '#6b7280' }}>Page {page}</span>
                    <button disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', cursor: page * 50 >= total ? 'default' : 'pointer', background: page * 50 >= total ? '#f3f4f6' : '#fff' }}>Next →</button>
                </div>
            )}

            {/* Override Modal */}
            {overrideModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: '20px', padding: '32px', width: '360px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ fontSize: '1.2rem', color: '#1f2937', marginBottom: '8px' }}>Override Progress</h3>
                        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
                            {overrideModal.quest.title} → <strong>{overrideModal.assignee.kid_username}</strong>
                        </p>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                            New Progress (max {overrideModal.quest.target_value})
                        </label>
                        <input
                            id="override-progress-input"
                            type="number"
                            min="0"
                            max={overrideModal.quest.target_value}
                            value={overrideProgress}
                            onChange={e => setOverrideProgress(e.target.value)}
                            style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '16px', boxSizing: 'border-box', marginBottom: '20px' }}
                        />
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => setOverrideModal(null)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', color: '#374151' }}>Cancel</button>
                            <button id="override-submit-btn" onClick={handleOverride} disabled={overrideLoading} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#6366f1', color: '#fff', fontWeight: '600', cursor: 'pointer' }}>
                                {overrideLoading ? 'Saving...' : 'Save Override'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminQuestDashboard;
